import React from 'react';
import i18next, { t } from 'i18next';
import { Data, Domain } from '@3nickels/data-modules';
import { PaymentApi } from '../../../api/apis/PaymentApi';
import {
  convertToTranslationKey,
  formatDateMMDDYYYY,
  formatDecimalDollars,
} from '../../../helpers/utilityFunctions';
import { Svgs } from '../../../assets/svg';
import { useNavigate } from 'react-router';

import { IMessageContext } from '../../../hooks/useMessage';
import { ICommandExecutor } from '@guidedchoice/scrinium';
import { getInterval } from './functions';
import { PlanCardPages } from './PlanCard';
import { Button } from '../../../components/buttons/Button';

export type PlanUserAction = 'select' | 'upgrade' | 'downgrade' | 'resubscribe' | 'unsubscribe';

const handleResubscribe = async (
  commands: ICommandExecutor,
  pricebookId: number,
  setLoading: (loading: boolean) => void,
  showMessage?: IMessageContext['showMessage']
) => {
  setLoading(true);
  try {
    await commands.execute(Data.Users.Commands.Resubscribe, pricebookId);
    showMessage?.(t('SubscriptionRestartedSuccessfully') as string);
  } catch (err) {
    console.error(err);
    showMessage?.(t('FailedToResubscribe') as string, 'error');
  } finally {
    setLoading(false);
  }
};

const handleUpgradeCheckout = async (
  commands: ICommandExecutor,
  pricebookId: number,
  setLoading: (loading: boolean) => void,
  showMessage?: IMessageContext['showMessage']
) => {
  setLoading(true);
  try {
    const stripeUrl = await commands.execute(
      Data.CheckoutSession.Commands.CreateAddOnCheckoutSession,
      pricebookId
    );
    window.location.replace(stripeUrl);
  } catch (err) {
    console.error(err);
    showMessage?.(t('FailedToUpgrade') as string, 'error');
    setLoading(false);
  }
};

const handleStandardCheckout = async (
  paymentApi: PaymentApi,
  pricebookId: number,
  setLoading: (loading: boolean) => void,
  showMessage?: IMessageContext['showMessage']
) => {
  setLoading(true);
  try {
    const stripeUrl = (await paymentApi.createSession(pricebookId)).data;
    window.location.replace(stripeUrl);
  } catch (err) {
    console.error(err);
    showMessage?.(t('FailedToSubscribe') as string, 'error');
    setLoading(false);
  }
};

function getDescription(plan: Domain.PricebookBundle) {
  const memberPays = plan.pricebook.paidBy === Domain.PaidByEnum.USER;
  const internalFreeTrial = plan.pricebook.metadata?.internalFreeTrial === 'true';
  const free =
    plan.prices[0].price === 0 &&
    memberPays &&
    plan.pricebook.pricebookName.includes('Free') &&
    !internalFreeTrial;
  const nickelsU = plan.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELSU;
  const freemium = Boolean(plan.pricebook.metadata?.freemium);
  const guidedChoice = plan.pricebook.productType === 'GUIDED_CHOICE';

  if (free) {
    return t('ChecklistsAndCalculatorsToCoverTheBasics');
  } else if (freemium) {
    return t('OneOnOneCoachingFinancialEducationAndGoalPlanning');
  } else if (nickelsU) {
    return t('OneOnOneCoachingFinancialEducationAndAdvice');
  } else if (guidedChoice) {
    return t('RetirementPlanningSavingForAndSpendingDuring');
  } else {
    return t('ExpertAdviceOnYourWholeFinancialPicture');
  }
}

function getIntervalText(intervalType: Domain.PaymentIntervalEnum) {
  switch (intervalType) {
    case Domain.PaymentIntervalEnum.DAY:
      return t('day');
    case Domain.PaymentIntervalEnum.WEEK:
      return t('week');
    case Domain.PaymentIntervalEnum.MONTH:
      return t('month');
    case Domain.PaymentIntervalEnum.YEAR:
      return t('year');
    default:
      return '';
  }
}

const getFeatureLabel = (feature: string) => {
  switch (feature) {
    case 'Spending overview':
      return 'Spendingoverview';
    default:
      return convertToTranslationKey(feature);
  }
};

const resolveFeatureIcon = (feature: string) => {
  const iconProps = { style: { marginRight: '20px' } };
  switch (feature) {
    case 'Mobile app access':
      return <Svgs.DisplayMediumCustomCellPhone {...iconProps} />;
    case 'One-on-one coaching':
      return <Svgs.DisplaySmallCustomMembers {...iconProps} />;
    case 'Live Q&A':
      return <Svgs.DisplaySmallCustomAdvice {...iconProps} />;
    case '7+ hours of educational videos':
      return <Svgs.DisplaySmallCustomVideoCamera {...iconProps} />;
    case 'Financial planning':
      return <Svgs.DisplaySmallCustomGoals {...iconProps} />;
    case 'Investment advice':
      return <Svgs.DisplaySmallCustomInvestment {...iconProps} />;
    case 'Account linking':
      return <Svgs.DisplaySmallCustomBank {...iconProps} />;
    case 'Spending overview':
      return <Svgs.DisplaySmallCustomLoans {...iconProps} />;
    case 'Money Basics course':
      return <Svgs.IconCollege {...iconProps} />;
    case 'Personalized tools':
      return <Svgs.DisplaySmallCustomCalculator {...iconProps} />;
    case 'Budgeting':
      return <Svgs.DisplaySmallCustomBudget {...iconProps} />;
    default:
      return <Svgs.DisplaySmallCustomBook {...iconProps} />;
  }
};

function getPlanInfo(
  plan: Domain.PricebookBundle,
  subscriptionInfo?: Domain.SubscriptionInfo,
  freeTrialStatus?: Data.Users.FreeTrialInfoDto
) {
  const { language } = i18next;
  const orgPays = plan.pricebook.paidBy === Domain.PaidByEnum.ORG;
  const memberPays = plan.pricebook.paidBy === Domain.PaidByEnum.USER;
  const isAddOn = plan.pricebook.addOn;
  const promotion = plan.prices.find((p) => p.introductionPeriodInDays > 1);
  const nickelsU = plan.pricebook.productType === Domain.PricebookProductTypeEnum.NICKELSU;
  const freemium = Boolean(plan.pricebook.metadata?.freemium);
  const internalFreeTrial = plan.pricebook.metadata?.internalFreeTrial === 'true';
  const inNickelsUPromo = subscriptionInfo?.status === 'trialing' && nickelsU ? true : false;
  const freeTrialDaysLeft = freeTrialStatus?.expirationDate
    ? Math.ceil(
        (new Date(freeTrialStatus.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
    : undefined;
  const isFree =
    plan.prices[0].price === 0 &&
    memberPays &&
    plan.pricebook.pricebookName.includes('Free') &&
    !internalFreeTrial;
  const basePrice = formatDecimalDollars(plan.prices[0].price / 100, language);
  const promoPrice = plan.prices[1]
    ? formatDecimalDollars(plan.prices[1].price / 100, language)
    : null;

  return {
    language,
    orgPays,
    memberPays,
    isAddOn,
    promotion,
    nickelsU,
    inNickelsUPromo,
    freemium,
    freeTrialDaysLeft,
    isFree,
    basePrice,
    promoPrice,
    pricebookName: plan.pricebook.pricebookName,
    pricebookId: plan.pricebook.id,
    productType: plan.pricebook.productType,
    artUrl: plan.pricebook.artUrl,
  };
}

function getPlanFootnotes(
  planInfo: ReturnType<typeof getPlanInfo>,
  pageContext: PlanCardPages,
  subscriptionInfo: Domain.SubscriptionInfo | undefined,
  adviceAddOnPricebook: Domain.PricebookBundle | undefined,
  addOnSubscription: Domain.SubscriptionInfo | undefined,
  inAdviceFreeTrial: boolean,
  userNeverSubscribedToExclusivePricebook: boolean | undefined,
  userIsUnderFreemiumModel: boolean | undefined,
  plan: Domain.PricebookBundle
) {
  if (planInfo.isFree || (planInfo.orgPays && !userIsUnderFreemiumModel)) {
    return { footnoteText: null };
  }

  const subscriptionStatus = subscriptionInfo?.mappedStatus;
  const addOnSubscriptionStatus = addOnSubscription?.mappedStatus;
  const addOnNeverActivated =
    !addOnSubscriptionStatus?.active &&
    !(addOnSubscriptionStatus?.cancelled || addOnSubscriptionStatus?.expired);

  const getPriceDetails = (priceIndex: number) => ({
    price: formatDecimalDollars(plan.prices[priceIndex].price / 100, planInfo.language),
    interval: getInterval(plan.prices[priceIndex].intervalType),
  });

  if (userIsUnderFreemiumModel && addOnNeverActivated) {
    const addOnPrice = formatDecimalDollars((adviceAddOnPricebook?.prices[0].price ?? 0) / 100);
    const addOnInterval = getInterval(adviceAddOnPricebook?.prices[0].intervalType);
    return {
      footnoteText: t('YoureNotCurrentlySubscribedRenewsAt', {
        price: addOnPrice,
        interval: addOnInterval,
      }),
    };
  }

  if (planInfo.promotion && !userIsUnderFreemiumModel) {
    const { price: priceAfterPromo, interval: intervalAfterPromo } = getPriceDetails(1);
    const introPeriodInMonths = Math.floor((planInfo.promotion.introductionPeriodInDays ?? 0) / 30);

    return {
      footnoteText: t('InitiallyAOneTimePayment', {
        promotionalPeriodInMonths: introPeriodInMonths,
        price: priceAfterPromo,
        interval: intervalAfterPromo,
      }),
    };
  }

  if (inAdviceFreeTrial && pageContext === 'ManageSubscription') {
    const advicePrice = formatDecimalDollars(14.99, planInfo.language);
    const footnoteText =
      planInfo.freeTrialDaysLeft === 1
        ? t('OneDayLeftOfFreeTrial', { price: advicePrice })
        : t('DaysLeftOfFreeTrial', { days: planInfo.freeTrialDaysLeft, price: advicePrice });

    return { footnoteText };
  }

  if (userNeverSubscribedToExclusivePricebook) {
    const priceIndex = planInfo.promotion ? 1 : 0;
    const { price, interval } = getPriceDetails(priceIndex);

    return {
      footnoteText: t('YoureNotCurrentlySubscribedRenewsAt', { price, interval }),
    };
  }

  const subscriptionStatusOfRecentSubscription = userIsUnderFreemiumModel
    ? addOnSubscriptionStatus
    : subscriptionStatus;
  const isCancelledOrExpired =
    subscriptionStatusOfRecentSubscription?.cancelled ||
    subscriptionStatusOfRecentSubscription?.expired;

  if (isCancelledOrExpired) {
    return {
      footnoteText: t('IfRestartedRenewsEvery', {
        interval: getInterval(subscriptionInfo?.interval),
      }),
    };
  }

  return {
    footnoteText: t('ThisSubscriptionRenewsAutomatically', {
      interval: getInterval(plan.prices[0].intervalType),
    }),
  };
}

function determineUserAction(
  isCurrentPlan: boolean | undefined,
  pageContext: PlanCardPages | undefined,
  isAddOnPlan: boolean,
  subscriptionStatus: Domain.MappedSubscriptionStatus | undefined,
  userIsUnderFreemiumModel: boolean | undefined,
  isFree: boolean,
  orgPays: boolean,
  orgDoesNotOfferFreePricebook: boolean | undefined,
  onlyOffersFreeAndOnePaidPricebook: boolean | undefined
): PlanUserAction | undefined {
  if (orgPays) return;

  switch (pageContext) {
    case 'ManageSubscription': {
      if (isAddOnPlan) {
        if (subscriptionStatus?.cancelled || subscriptionStatus?.expired) return 'resubscribe';
        if (subscriptionStatus?.active) return 'unsubscribe';
        return 'upgrade';
      }

      if (!userIsUnderFreemiumModel) {
        return;
      } else {
        return isCurrentPlan ? 'unsubscribe' : undefined;
      }
    }

    case 'ChangePlan': {
      if (isCurrentPlan) {
        if (orgDoesNotOfferFreePricebook) return 'unsubscribe';
        if (
          onlyOffersFreeAndOnePaidPricebook &&
          subscriptionStatus?.cancelled &&
          !subscriptionStatus?.expired &&
          !isFree
        ) {
          return;
        }

        return;
      }

      if (onlyOffersFreeAndOnePaidPricebook && !isFree) {
        if (subscriptionStatus?.expired) return 'resubscribe';
        if (!subscriptionStatus?.active && !subscriptionStatus?.cancelled) return 'upgrade';
      }

      if (subscriptionStatus?.cancelled && subscriptionStatus?.expired && !isFree)
        return 'resubscribe';

      if (isFree && !isCurrentPlan) return 'downgrade';

      return 'select';
    }

    default:
      return;
  }
}

function getPlanSubtexts(
  planInfo: ReturnType<typeof getPlanInfo>,
  plan: Domain.PricebookBundle,
  pageContext: PlanCardPages | undefined,
  subscriptionInfo: Domain.SubscriptionInfo | undefined,
  inAdviceFreeTrial: boolean,
  userNeverSubscribedToExclusivePricebook: boolean | undefined,
  userIsUnderFreemiumModel: boolean | undefined,
  userAction: PlanUserAction | undefined
) {
  const defaults = {
    paymentSubtext: null as string | null,
    paymentSubtextColor: 'secondary',
    titleSubtext: null as string | null,
    titleSubtextColor: 'error.main',
  };

  const getNextPaymentText = () =>
    t('NextPaymentOfPriceOnDate', {
      price: formatDecimalDollars((subscriptionInfo?.price ?? 0) / 100, planInfo.language),
      date: formatDateMMDDYYYY(subscriptionInfo?.expirationDate ?? ''),
    });

  if (planInfo.orgPays) {
    if (!planInfo.isAddOn) {
      return {
        ...defaults,
        paymentSubtext: t('YourOrganizationIsSponsoringYourSubscription'),
      };
    }
    return defaults;
  }

  if (planInfo.inNickelsUPromo && pageContext === 'ManageSubscription') {
    return {
      ...defaults,
      paymentSubtext: getNextPaymentText(),
    };
  }

  if (inAdviceFreeTrial && !userIsUnderFreemiumModel) {
    return {
      ...defaults,
      paymentSubtext:
        planInfo.freeTrialDaysLeft === 1
          ? t('1DayLeftOfFreeTrial')
          : t('XDaysLeftOfFreeTrial', { x: planInfo.freeTrialDaysLeft }),
    };
  }

  const subscriptionStatus = subscriptionInfo?.mappedStatus;

  if (
    (subscriptionStatus?.cancelled || subscriptionStatus?.expired) &&
    subscriptionInfo?.origin !== Domain.PaymentOriginEnum.FREE_TRIAL &&
    pageContext === 'ManageSubscription'
  ) {
    const text = subscriptionStatus.expired ? t('Expired') : t('Expires');
    const expirationText = `${text} ${formatDateMMDDYYYY(subscriptionInfo?.expirationDate ?? '')}`;

    if (userIsUnderFreemiumModel) {
      return {
        ...defaults,
        titleSubtext: expirationText,
        paymentSubtext: t('AutoRenewsMonthlyIfRestarted'),
        paymentSubtextColor: 'primary',
      };
    }

    return {
      ...defaults,
      paymentSubtext: expirationText,
      paymentSubtextColor: 'error.main',
    };
  }

  if (planInfo.promotion && pageContext === 'ChangePlan') {
    return {
      ...defaults,
      paymentSubtext: t('oneTimePayment'),
      paymentSubtextColor: 'primary',
    };
  }

  if (
    subscriptionInfo?.pricebookId === planInfo.pricebookId &&
    (subscriptionStatus?.active || (subscriptionStatus?.cancelled && !subscriptionStatus?.expired))
  ) {
    if (userIsUnderFreemiumModel && planInfo.isAddOn) {
      return {
        ...defaults,
        paymentSubtext: getNextPaymentText(),
        paymentSubtextColor: 'primary',
      };
    }

    if (pageContext === 'ChangePlan' && !planInfo.isFree && !planInfo.promotion) {
      const intervalType = plan.prices[0].intervalType;
      const paymentSubtext =
        intervalType === Domain.PaymentIntervalEnum.YEAR
          ? t('autoRenewsAnnually')
          : t('autoRenewsMonthly');

      return {
        ...defaults,
        paymentSubtext,
        paymentSubtextColor: 'primary',
      };
    }

    if (!userIsUnderFreemiumModel) {
      return {
        ...defaults,
        paymentSubtext: getNextPaymentText(),
      };
    }

    return defaults;
  }

  const userCanPay =
    (userAction === 'upgrade' || userAction === 'select' || userAction === 'resubscribe') &&
    !planInfo.isFree &&
    !planInfo.promotion;

  if (userCanPay && pageContext === 'ChangePlan') {
    const intervalType = plan.prices[0].intervalType;
    const paymentSubtext =
      intervalType === Domain.PaymentIntervalEnum.YEAR
        ? t('autoRenewsAnnually')
        : t('autoRenewsMonthly');

    return {
      ...defaults,
      paymentSubtext,
      paymentSubtextColor: 'primary',
    };
  }

  if (userNeverSubscribedToExclusivePricebook && pageContext === 'ManageSubscription') {
    return {
      ...defaults,
      paymentSubtext: t('NotCurrentlySubscribed'),
      paymentSubtextColor: 'error.main',
    };
  }

  return defaults;
}

type ButtonConfig = {
  label: string;
  variant: 'outlined' | 'contained';
  onClick: () => void | Promise<void>;
};

function getPlanActionButton(
  userAction: PlanUserAction | undefined,
  mobile: boolean,
  planInfo: ReturnType<typeof getPlanInfo>,
  plan: Domain.PricebookBundle,
  paymentApi: PaymentApi,
  commands: ICommandExecutor,
  setUnsubscribeModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  subscriptionInfo: Domain.SubscriptionInfo | undefined,
  setLoading: (loading: boolean) => void,
  setResubscribeModalOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  showMessage?: IMessageContext['showMessage']
) {
  if (planInfo.orgPays || !userAction) return null;

  const buttonConfigs: Record<PlanUserAction, ButtonConfig> = {
    unsubscribe: {
      label: t('Unsubscribe'),
      variant: 'outlined',
      onClick: () => setUnsubscribeModalOpen(true),
    },
    downgrade: {
      label: t('Downgrade'),
      variant: 'outlined',
      onClick: () => setUnsubscribeModalOpen(true),
    },
    resubscribe: {
      label: t('Resubscribe'),
      variant: 'contained',
      onClick: async () => {
        if (subscriptionInfo?.origin === Domain.PaymentOriginEnum.STRIPE) {
          setResubscribeModalOpen?.(true);
          return;
        }
        await handleResubscribe(commands, plan.pricebook.id, setLoading, showMessage);
      },
    },
    select: {
      label: t('SelectPlan'),
      variant: 'contained',
      onClick: async () =>
        await handleStandardCheckout(paymentApi, plan.pricebook.id, setLoading, showMessage),
    },
    upgrade: {
      label: t('Upgrade'),
      variant: 'contained',
      onClick: async () =>
        await handleUpgradeCheckout(commands, plan.pricebook.id, setLoading, showMessage),
    },
  };

  const config = buttonConfigs[userAction];

  return (
    <Button
      fullWidth={mobile}
      style={{ width: mobile ? undefined : '50%', marginTop: '10px' }}
      color='primary'
      variant={config.variant}
      label={config.label}
      onClick={config.onClick}
    />
  );
}

function getFootnoteElement(
  pageContext: PlanCardPages,
  footnoteCounter: number,
  plan: Domain.PricebookBundle,
  subscription: Domain.SubscriptionInfo | undefined,
  inAdviceFreeTrial: boolean,
  freeTrialStatus?: Data.Users.FreeTrialInfoDto,
  userNeverSubscribedToExclusivePricebook?: boolean,
  userIsUnderFreemiumModel?: boolean,
  adviceAddOnPricebook?: Domain.PricebookBundle,
  addOnSubscription?: Domain.SubscriptionInfo | undefined
) {
  const planInfo = getPlanInfo(plan, subscription, freeTrialStatus);

  const { footnoteText } = getPlanFootnotes(
    planInfo,
    pageContext,
    subscription,
    adviceAddOnPricebook,
    addOnSubscription,
    inAdviceFreeTrial,
    userNeverSubscribedToExclusivePricebook,
    userIsUnderFreemiumModel,
    plan
  );

  if (footnoteText) {
    footnoteCounter++;
  }

  return { footnoteCounter, footnoteText };
}

function getSubtextsAndActionButton(
  planInfo: ReturnType<typeof getPlanInfo>,
  plan: Domain.PricebookBundle,
  subscriptionInfo: Domain.SubscriptionInfo | undefined,
  isCurrentPlan: boolean | undefined,
  pageContext: PlanCardPages | undefined,
  mobile: boolean,
  paymentApi: PaymentApi,
  commands: ICommandExecutor,
  setUnsubscribeModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  userIsUnderFreemiumModel: boolean | undefined,
  orgDoesNotOfferFreePricebook: boolean | undefined,
  inAdviceFreeTrial: boolean,
  userNeverSubscribedToExclusivePricebook: boolean | undefined,
  onlyOffersFreeAndOnePaidPricebook: boolean | undefined,
  setLoading: (loading: boolean) => void,
  showMessage?: IMessageContext['showMessage'],
  setResubscribeModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
) {
  const subscriptionStatus = subscriptionInfo?.mappedStatus;
  const userAction = determineUserAction(
    isCurrentPlan,
    pageContext,
    planInfo.isAddOn,
    subscriptionStatus,
    userIsUnderFreemiumModel,
    planInfo.isFree,
    planInfo.orgPays,
    orgDoesNotOfferFreePricebook,
    onlyOffersFreeAndOnePaidPricebook
  );

  const { paymentSubtext, paymentSubtextColor, titleSubtext, titleSubtextColor } = getPlanSubtexts(
    planInfo,
    plan,
    pageContext,
    subscriptionInfo,
    inAdviceFreeTrial,
    userNeverSubscribedToExclusivePricebook,
    userIsUnderFreemiumModel,
    userAction
  );

  const actionButton = getPlanActionButton(
    userAction,
    mobile,
    planInfo,
    plan,
    paymentApi,
    commands,
    setUnsubscribeModalOpen,
    subscriptionInfo,
    setLoading,
    setResubscribeModalOpen,
    showMessage
  );

  return { paymentSubtext, paymentSubtextColor, titleSubtext, titleSubtextColor, actionButton };
}

const getPrimaryButtonDetails = (
  plan: Domain.PricebookBundle,
  subscription: Domain.SubscriptionInfo | undefined,
  orgPaysPricebook: Domain.PricebookBundle | undefined,
  commands: ICommandExecutor,
  paymentApi: PaymentApi,
  multipleProducts: boolean,
  orgOnlyOffersFreePricebook: boolean,
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setLoading: (loading: boolean) => void,
  setResubscribeModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  showMessage: IMessageContext['showMessage']
) => {
  const navigate = useNavigate();
  const status = subscription?.mappedStatus;
  const subscriptionNeverActivated =
    typeof status === 'undefined' || subscription?.origin === Domain.PaymentOriginEnum.FREE_TRIAL;

  const subscribe = subscriptionNeverActivated && !multipleProducts && !orgOnlyOffersFreePricebook;
  const resubscribe =
    (status?.cancelled || status?.expired) &&
    subscription?.origin !== Domain.PaymentOriginEnum.FREE_TRIAL &&
    !multipleProducts;

  if (multipleProducts)
    return {
      text: t('ChangePlan'),
      onClick: () => navigate('/settings/manage-subscription/change-plan'),
    };

  if (orgPaysPricebook) return;

  if (
    subscription?.status === 'active' &&
    subscription?.origin !== Domain.PaymentOriginEnum.FREE_TRIAL
  )
    return { text: 'Unsubscribe', onClick: () => setModalOpen(true) };

  if (subscribe)
    return {
      text: 'Subscribe',
      onClick: async () => {
        const stripeUrl = await paymentApi.createSession(plan.pricebook.id);
        window.location.replace(stripeUrl.data);
      },
    };

  if (resubscribe) {
    return {
      text: 'Resubscribe',
      onClick: async () => {
        if (subscription?.origin === Domain.PaymentOriginEnum.STRIPE) {
          setResubscribeModalOpen(true);
          return;
        }
        await handleResubscribe(commands, plan.pricebook.id, setLoading, showMessage);
      },
    };
  }
};

const resolveShowManagePayments = (
  orgPaysPricebook: Domain.PricebookBundle | undefined,
  addOns: boolean,
  orgOnlyOffersFreePricebook: boolean,
  subscriptionInfoData: Domain.SubscriptionInfo[] | undefined
) => {
  const orgWithNoAddOns = orgPaysPricebook && !addOns;
  const orgWithAddOns = orgPaysPricebook && addOns;
  const noSubscriptionHistory = !subscriptionInfoData || subscriptionInfoData.length === 0;

  if (orgWithNoAddOns) return false;
  if (orgOnlyOffersFreePricebook) return false;
  if (noSubscriptionHistory && !orgWithAddOns) return false;
  return true;
};

export {
  getDescription,
  getIntervalText,
  getFeatureLabel,
  resolveFeatureIcon,
  getPlanInfo,
  determineUserAction,
  getPlanSubtexts,
  getPlanActionButton,
  getFootnoteElement,
  getSubtextsAndActionButton,
  getPrimaryButtonDetails,
  resolveShowManagePayments,
  handleResubscribe,
  handleUpgradeCheckout,
  handleStandardCheckout,
};
